import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import type { FiltersInput } from '@app/@classes/abstract/items-service-interface';
import { CbRoutesUtils } from '@app/@classes/routes/cb-routes-utils';
import type { Tables, Views } from '@app/@classes/supabase';
import {
  ALERT_ICON_MAP,
  ALERT_LABEL_MAP,
} from '@app/constants/alert.const';
import { DEADLINE_SUBJECT_TYPE_ICON_MAP } from '@app/constants/deadlines-events.const';
import type { NotificationAction } from '@app/widgets/base/notification/notification/notification.component';
import { ToastService } from '@app/widgets/base/notification/toast.service';
import { DialogService } from '@app/widgets/base/dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { NotificationsService } from '../data/notifications.service';
import { NotificationsWithDetailsService } from '../data/notifications-with-details.service';
import { AuthStateService } from './auth-state.service';
import { TenantModulesAccessService } from './tenant-modules-access.service';

type Office = 'warehouse' | 'administration' | 'planning' | 'customer-portal';
type Entity = Views<'notifications_with_details'>;
type NotificationRow = Tables<'notifications'>;

/** Notifica magazzino in attesa di essere mostrata su /w/ */
type PendingWarehouseNotification = {
  id: string;
  queuedAt: number;
};

const PENDING_WAREHOUSE_NOTIFICATIONS_KEY =
  'cb_pending_warehouse_notifications';
const PENDING_WAREHOUSE_NOTIFICATIONS_TTL_MS = 30 * 60 * 1000;
/** Numero massimo di toast simultanei gestiti dal ToastService. */
const MAX_SIMULTANEOUS_TOASTS = 5;
const PENDING_SUMMARY_TOAST_KEY = 'cb_warehouse_pending_summary';

/**
 * Service per gestire lo stato delle notifiche in modo globale.
 * Mantiene traccia del conteggio delle notifiche non lette.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsStateService {
  private readonly notificationsService = inject(
    NotificationsWithDetailsService,
  );
  private readonly notificationsBaseService = inject(NotificationsService);
  private readonly tenantModulesAccess = inject(TenantModulesAccessService);
  private readonly authState = inject(AuthStateService);
  private readonly toastService = inject(ToastService);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  /** Conteggio delle notifiche non lette */
  public readonly unreadCount = signal<number>(0);

  /** Indica se ci sono notifiche non lette */
  public readonly hasUnread = computed(() => this.unreadCount() > 0);

  /** Evita flush concorrenti (effect auth + NavigationEnd) sulla stessa coda. */
  private isFlushingPending = false;

  constructor() {
    // Sottoscrivi agli aggiornamenti realtime per mantenere il conteggio aggiornato
    this.notificationsService.realtimeUpdate
      .pipe(takeUntilDestroyed())
      .subscribe((update) => {
        // Ricarica il conteggio quando c'è un aggiornamento realtime
        const office = this.tenantModulesAccess.currentOffice();
        if (office) {
          this.loadUnreadCount(office);
        }

        // Mostra un toast per le nuove notifiche pertinenti all'ufficio corrente
        if (update.type === 'INSERT') {
          this.handleRealtimeInsert(
            update.data as Partial<NotificationRow>,
            office,
          );
        }
      });

    // Il conteggio non letto viene già ricaricato da AppComponent su NavigationEnd
    // (con la stessa guardia di path). Qui gestiamo solo coda toast e reset badge.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.isOnWarehouseDesktopPath()) {
          this.flushPendingWarehouseNotifications();
        } else {
          this.unreadCount.set(0);
          this.purgeExpiredPendingWarehouseNotifications();
        }
      });

    // Riprova il flush quando l'auth diventa disponibile (es. refresh diretto su
    // /w/, dove il primo NavigationEnd può precedere la valorizzazione di tenant/utente).
    effect(() => {
      const tenantId = this.authState.currentTenantId();
      const userId = this.authState.currentUserId();
      if (tenantId && userId && this.isOnWarehouseDesktopPath()) {
        this.flushPendingWarehouseNotifications();
      }
    });
  }

  /**
   * Notifiche magazzino (alert, DDT palmare, step saltati, ecc.): solo su /w/.
   * Esclude esplicitamente /wp/ (palmare) e gli altri uffici.
   */
  private isOnWarehouseDesktopPath(): boolean {
    const url = this.router.url.split('?')[0]?.split('#')[0] ?? '';
    const warehouseBasePath = CbRoutesUtils.paths.w.path;
    return (
      url === warehouseBasePath || url.startsWith(`${warehouseBasePath}/`)
    );
  }

  /**
   * Gestisce l'inserimento realtime di una notifica: se è pertinente
   * all'ufficio corrente ed è destinata all'utente, mostra un toast.
   */
  private handleRealtimeInsert(
    row: Partial<NotificationRow>,
    office: Office | undefined,
  ): void {
    if (!row?.id) {
      return;
    }

    // Già letta (es. inserita come letta): nessun toast
    if (row.is_read) {
      return;
    }

    // Destinatario: solo notifiche broadcast (user_id null) o destinate a me
    const currentUserId = this.authState.currentUserId();
    if (row.user_id && row.user_id !== currentUserId) {
      return;
    }

    const isWarehouseNotification = this.matchesOffice('warehouse', row);

    if (isWarehouseNotification) {
      if (this.isOnWarehouseDesktopPath()) {
        this.fetchAndShowNotificationToast(row.id);
      } else {
        this.enqueuePendingWarehouseNotification(row.id);
      }
      return;
    }

    if (!office || !this.matchesOffice(office, row)) {
      return;
    }

    this.fetchAndShowNotificationToast(row.id);
  }

  private fetchAndShowNotificationToast(notificationId: string): void {
    this.notificationsService
      .getById(notificationId)
      .pipe(take(1))
      .subscribe({
        next: (entity) => this.showNotificationToast(entity),
        error: () => {
          this.removePendingWarehouseNotification(notificationId);
        },
      });
  }

  private getPendingWarehouseNotificationsStorageKey(): string | null {
    const tenantId = this.authState.currentTenantId();
    const userId = this.authState.currentUserId();
    if (!tenantId || !userId) {
      return null;
    }
    return `${PENDING_WAREHOUSE_NOTIFICATIONS_KEY}_${tenantId}_${userId}`;
  }

  private readPendingWarehouseNotifications(): PendingWarehouseNotification[] {
    const key = this.getPendingWarehouseNotificationsStorageKey();
    if (!key) {
      return [];
    }

    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return [];
      }
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed.filter(
        (item): item is PendingWarehouseNotification =>
          typeof item === 'object' &&
          item !== null &&
          typeof (item as PendingWarehouseNotification).id === 'string' &&
          typeof (item as PendingWarehouseNotification).queuedAt === 'number',
      );
    } catch {
      return [];
    }
  }

  private writePendingWarehouseNotifications(
    items: PendingWarehouseNotification[],
  ): void {
    const key = this.getPendingWarehouseNotificationsStorageKey();
    if (!key) {
      return;
    }

    if (items.length === 0) {
      localStorage.removeItem(key);
      return;
    }

    localStorage.setItem(key, JSON.stringify(items));
  }

  private purgeExpiredPendingWarehouseNotifications(): PendingWarehouseNotification[] {
    const now = Date.now();
    const valid = this.readPendingWarehouseNotifications().filter(
      (item) => now - item.queuedAt <= PENDING_WAREHOUSE_NOTIFICATIONS_TTL_MS,
    );
    this.writePendingWarehouseNotifications(valid);
    return valid;
  }

  private enqueuePendingWarehouseNotification(notificationId: string): void {
    const now = Date.now();
    const pending = this.purgeExpiredPendingWarehouseNotifications();
    if (pending.some((item) => item.id === notificationId)) {
      return;
    }

    this.writePendingWarehouseNotifications([
      ...pending,
      { id: notificationId, queuedAt: now },
    ]);
  }

  private removePendingWarehouseNotification(notificationId: string): void {
    const pending = this.readPendingWarehouseNotifications().filter(
      (item) => item.id !== notificationId,
    );
    this.writePendingWarehouseNotifications(pending);
  }

  /**
   * Mostra i toast in coda all'ingresso su /w/; scarta quelli oltre 30 min,
   * quelli già letti e quelli non più esistenti. Se sono più del massimo
   * mostrabile, mostra i più recenti + un toast riepilogo verso il centro notifiche.
   */
  private flushPendingWarehouseNotifications(): void {
    if (this.isFlushingPending) {
      return;
    }

    const pending = this.purgeExpiredPendingWarehouseNotifications();
    if (pending.length === 0) {
      return;
    }

    const ids = pending.map((item) => item.id);
    this.isFlushingPending = true;

    this.notificationsService
      .getMany({
        filters: {
          id: {
            active: true,
            column: 'id',
            operator: 'in',
            value: ids,
          },
          is_read: {
            active: true,
            column: 'is_read',
            operator: 'is',
            value: false,
          },
        },
      })
      .pipe(take(1))
      .subscribe({
        next: (entities) => {
          this.isFlushingPending = false;

          // Tutte le pending sono state processate (mostrate, lette o rimosse):
          // svuoto la coda così non vengono riproposte.
          this.writePendingWarehouseNotifications([]);

          // Più recenti prima, in base all'ordine di accodamento.
          const ordered = ids
            .map((id) => entities.find((entity) => entity.id === id))
            .filter((entity): entity is Entity => entity != null)
            .reverse();

          if (ordered.length <= MAX_SIMULTANEOUS_TOASTS) {
            for (const entity of ordered) {
              this.showNotificationToast(entity);
            }
            return;
          }

          const toShow = ordered.slice(0, MAX_SIMULTANEOUS_TOASTS - 1);
          for (const entity of toShow) {
            this.showNotificationToast(entity);
          }
          this.showPendingSummaryToast(ordered.length - toShow.length);
        },
        error: () => {
          // Mantengo la coda: si riprova al prossimo accesso su /w/.
          this.isFlushingPending = false;
        },
      });
  }

  /** Toast riepilogo per le notifiche magazzino eccedenti il limite di toast. */
  private showPendingSummaryToast(remaining: number): void {
    this.toastService.showMessage({
      key: PENDING_SUMMARY_TOAST_KEY,
      title: this.translate.instant('NOTIFICATIONS.TOAST.MORE_TITLE'),
      message: this.translate.instant('NOTIFICATIONS.TOAST.MORE_MESSAGE', {
        count: remaining,
      }),
      icon: 'cb-solid-rounded-notification-square',
      color: 'info',
      duration: 0,
      translate: false,
      actions: [
        {
          label: this.translate.instant('NOTIFICATIONS.TOAST.OPEN_CENTER'),
          color: 'brand',
          callback: () => {
            this.toastService.closeByKey(PENDING_SUMMARY_TOAST_KEY);
            this.openNotificationCenter();
          },
        },
      ],
    });
  }

  /** Apre il centro notifiche (stessa modalità del popover). */
  private openNotificationCenter(): void {
    import(
      '@app/widgets/app/notification-center-dialog/notification-center-dialog.component'
    ).then(({ NotificationCenterDialogComponent }) => {
      this.dialogService.open(NotificationCenterDialogComponent, {
        width: '900px',
      });
    });
  }

  /**
   * Replica esattamente il filtro per ufficio usato dal popover/badge.
   */
  private matchesOffice(
    office: Office,
    row: Partial<NotificationRow>,
  ): boolean {
    switch (office) {
      case 'warehouse':
        // Alert E notifiche generiche (no alert, no deadline)
        return (
          row.alert_id != null ||
          (row.alert_id == null && row.deadline_id == null)
        );
      case 'administration':
        // Solo scadenze (deadline)
        return row.deadline_id != null;
      default:
        // planning / customer-portal: nessuna notifica
        return false;
    }
  }

  /**
   * Costruisce e mostra un toast persistente per la notifica arricchita,
   * con azioni "Apri" (se collegata a un'entità) e "Segna come letta".
   */
  private showNotificationToast(entity: Entity): void {
    const id = entity.id;
    if (!id) {
      return;
    }

    let title: string;
    let message: string;
    let icon: string;
    let color: 'brand' | 'success' | 'warning' | 'error' | 'info' | 'gray';

    if (entity.deadline_id) {
      title =
        entity.template_name ??
        entity.title ??
        this.translate.instant('COMMON.ALERT');
      message = entity.subject_name ?? entity.message ?? '';
      icon =
        DEADLINE_SUBJECT_TYPE_ICON_MAP[entity.type ?? ''] ??
        'cb-solid-rounded-calendar-03';
      color = 'info';
    } else if (entity.alert_id) {
      const type = entity.type ?? '';
      title = this.translate.instant(ALERT_LABEL_MAP[type] ?? 'COMMON.ALERT');
      message = entity.message ?? '';
      icon = ALERT_ICON_MAP[type] ?? 'cb-solid-rounded-alert-circle';
      color = 'warning';
    } else {
      title = entity.title ?? '';
      message = entity.message ?? '';
      icon = 'cb-solid-rounded-alert-circle';
      color = 'warning';
    }

    const actions: NotificationAction[] = [];

    // "Apri" solo se c'è un'entità collegata apribile
    if (entity.outgoing_order_id || entity.pallet_id) {
      actions.push({
        label: this.translate.instant('NOTIFICATIONS.TOAST.OPEN'),
        color: 'brand',
        callback: () => this.openNotificationTarget(entity),
      });
    }

    // "Segna come letta"
    actions.push({
      label: this.translate.instant('NOTIFICATIONS.TOAST.MARK_AS_READ'),
      color: 'success',
      callback: () => {
        this.markAsRead(id);
        this.toastService.closeByKey(id);
      },
    });

    this.removePendingWarehouseNotification(id);

    this.toastService.showMessage({
      key: id,
      title,
      message,
      icon,
      color,
      actions,
      duration: 0, // persistente fino ad azione dell'utente
      translate: false,
    });
  }

  /**
   * Apre il dialog collegato alla notifica (ordine in uscita o pallet).
   */
  private openNotificationTarget(entity: Entity): void {
    if (entity.outgoing_order_id) {
      const orderId = entity.outgoing_order_id;
      import(
        '@app/modules/orders/dialogs/outgoing-order-dialog/outgoing-order-dialog.component'
      ).then(({ OutgoingOrderDialogComponent }) => {
        this.dialogService.open(OutgoingOrderDialogComponent, {
          data: { id: orderId },
          height: '100%',
          position: 'right',
        });
      });
    } else if (entity.pallet_id) {
      const palletId = entity.pallet_id;
      import(
        '@app/modules/warehouse_office/warehouse/dialogs/pallet-details-dialog/pallet-details-dialog.component'
      ).then(({ PalletDetailsDialogComponent }) => {
        this.dialogService.open(PalletDetailsDialogComponent, {
          data: { palletId },
          height: '100%',
          position: 'right',
        });
      });
    }
  }

  /**
   * Marca una notifica come letta e aggiorna il conteggio.
   */
  private markAsRead(notificationId: string): void {
    this.removePendingWarehouseNotification(notificationId);

    this.notificationsBaseService
      .putOne({
        id: notificationId,
        is_read: true,
      })
      .pipe(take(1))
      .subscribe(() => {
        this.decrementUnreadCount();
      });
  }

  /**
   * Carica il conteggio delle notifiche non lette per l'office specificato.
   * Usa gli stessi filtri del NotificationPopoverComponent.
   */
  public loadUnreadCount(office: Office): void {
    if (office === 'warehouse' && !this.isOnWarehouseDesktopPath()) {
      this.unreadCount.set(0);
      return;
    }

    // Costruisci i filtri in base all'office
    const filtersInput: FiltersInput<Entity> = {
      filters: {
        is_read: {
          active: true,
          column: 'is_read',
          operator: 'is',
          value: false,
        },
      },
    };

    // Aggiungi filtro specifico per office
    switch (office) {
      case 'warehouse':
        // Per warehouse: alert E notifiche generiche (ordine con step saltati, ecc.)
        filtersInput.filters!['alert_id'] = {
          active: true,
          operator: 'or',
          value: [
            {
              active: true,
              column: 'alert_id',
              operator: 'nis',
              value: null,
            },
            {
              active: true,
              operator: 'and',
              value: [
                {
                  active: true,
                  column: 'alert_id',
                  operator: 'is',
                  value: null,
                },
                {
                  active: true,
                  column: 'deadline_id',
                  operator: 'is',
                  value: null,
                },
              ],
            },
          ],
        };
        break;
      case 'administration':
        // Per administration: mostra solo deadline (deadline_id IS NOT NULL)
        filtersInput.filters!['deadline_id'] = {
          active: true,
          column: 'deadline_id',
          operator: 'nis',
          value: null,
        };
        break;
      case 'planning':
      case 'customer-portal':
        // Per planning e customer-portal non ci sono notifiche specifiche
        this.unreadCount.set(0);
        return;
    }

    this.notificationsService
      .getCount(filtersInput)
      .pipe(take(1))
      .subscribe((count) => {
        this.unreadCount.set(count);
      });
  }

  /**
   * Aggiorna il conteggio delle notifiche non lette.
   * Chiamato dal NotificationPopoverComponent quando carica i dati.
   */
  public setUnreadCount(count: number): void {
    this.unreadCount.set(count);
  }

  /**
   * Decrementa il conteggio quando una notifica viene letta.
   */
  public decrementUnreadCount(): void {
    this.unreadCount.update((count) => Math.max(0, count - 1));
  }
}
